import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Scale, Copy, Check } from 'lucide-react';
import PageLayout from '../layout/PageLayout';

const LicensePage: React.FC = () => {
  const [copied, setCopied] = React.useState(false);

  const licenseText = `Public Domain

All code, documentation, and other creative works in theweebcoders repositories
are released into the public domain.

You are free to:
- Use the code for any purpose
- Modify the code in any way
- Distribute the code
- Use it commercially
- Not give attribution (though it's appreciated!)
- Do literally whatever you want with it

We make no warranties and accept no liability. The code is provided "as is".

No licenses. No restrictions. Pure chaos. Pure freedom.

Duration: This dedication is granted irrevocably, in perpetuity, perpetually,
retroactively, in divergent world lines, in non-divergent world lines, in
parallel world lines, in the event of a grandfather paradox, in the event of a
grandmother paradox, any other paradox, and Tuesdays.

It binds the author and every heir, estate, successor, and would-be
rightsholder in every jurisdiction, including ones where copyright is inherited
and someone thinks that means they get a veto. They don't. Death does not
un-release this. Succession does not un-release this. There is no take-back.`;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(licenseText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <PageLayout>
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex justify-center mb-6">
              <div
                className="p-4 rounded-full"
                style={{ backgroundColor: 'var(--color-primary)' + '20' }}
              >
                <Scale size={48} style={{ color: 'var(--color-primary)' }} />
              </div>
            </div>
            <h1 className="text-5xl md:text-6xl font-theme font-bold mb-6 text-gradient">
              License
            </h1>
            <p className="text-2xl md:text-3xl mb-4" style={{ color: 'var(--color-text)' }}>
              Everything Here Is Public Domain
            </p>
            <p className="text-lg" style={{ color: 'var(--color-text-secondary)' }}>
              Not GPL. Not MIT. Not even copyleft. <strong>Public. Domain. Baby.</strong>
            </p>
          </motion.div>
        </div>
      </section>

      {/* The License Text */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="p-8 rounded-xl font-mono text-sm md:text-base relative"
            style={{
              backgroundColor: 'var(--color-bg-secondary)',
              borderColor: 'var(--color-border)',
              borderWidth: '2px',
              borderStyle: 'solid'
            }}
          >
            <button
              onClick={copyToClipboard}
              className="absolute top-4 right-4 p-2 rounded-lg transition-all hover:scale-110"
              style={{
                backgroundColor: 'var(--color-primary)' + '20',
                color: 'var(--color-primary)'
              }}
              aria-label="Copy license text"
            >
              {copied ? <Check size={20} /> : <Copy size={20} />}
            </button>

            <pre
              className="whitespace-pre-wrap"
              style={{ color: 'var(--color-text)' }}
            >
              {licenseText}
            </pre>
          </motion.div>

          <div className="mt-4 text-center">
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              <a
                href="https://github.com/theweebcoders/.github/raw/main/there_is_no_license.jpg"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
                style={{ color: 'var(--color-primary)' }}
              >
                See the visual representation →
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* What This Means */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-theme font-bold mb-8 text-center text-gradient">
            What Does This Actually Mean?
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="p-6 rounded-xl"
              style={{
                backgroundColor: 'var(--color-bg-secondary)',
                borderColor: 'var(--color-border)',
                borderWidth: '1px',
                borderStyle: 'solid'
              }}
            >
              <h3 className="text-xl font-theme font-bold mb-4" style={{ color: 'var(--color-success)' }}>
                ✓ You CAN
              </h3>
              <ul className="space-y-2">
                {[
                  'Use it commercially',
                  'Modify it however you want',
                  'Distribute it',
                  'Sell it',
                  'Claim you wrote it (we don\'t recommend this morally, but legally you can)',
                  'Not give attribution',
                  'Fork it and never contribute back',
                  'Use it in proprietary software',
                  'Keep using it after we\'re dead and someone in Germany inherits the repo',
                  'Literally do whatever you want'
                ].map((item, i) => (
                  <li key={i} className="flex items-start space-x-2">
                    <span style={{ color: 'var(--color-success)' }}>✓</span>
                    <span style={{ color: 'var(--color-text)' }}>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="p-6 rounded-xl"
              style={{
                backgroundColor: 'var(--color-bg-secondary)',
                borderColor: 'var(--color-border)',
                borderWidth: '1px',
                borderStyle: 'solid'
              }}
            >
              <h3 className="text-xl font-theme font-bold mb-4" style={{ color: 'var(--color-error)' }}>
                ✗ You DON'T Have To
              </h3>
              <ul className="space-y-2">
                {[
                  'Give attribution',
                  'Keep the same license',
                  'Share your modifications',
                  'Ask for permission',
                  'Include copyright notices',
                  'Pay us anything',
                  'Even acknowledge we exist',
                  'Ask the author\'s heirs for permission',
                  'Follow any rules at all'
                ].map((item, i) => (
                  <li key={i} className="flex items-start space-x-2">
                    <span style={{ color: 'var(--color-error)' }}>✗</span>
                    <span style={{ color: 'var(--color-text)' }}>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Public Domain? */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="p-8 rounded-xl"
            style={{
              backgroundColor: 'var(--color-bg-secondary)',
              borderColor: 'var(--color-border)',
              borderWidth: '1px',
              borderStyle: 'solid'
            }}
          >
            <h2 className="text-2xl md:text-3xl font-theme font-bold mb-6 text-gradient">
              Why Public Domain?
            </h2>
            <div className="space-y-4" style={{ color: 'var(--color-text)' }}>
              <p>
                Because licenses are complicated, restrictive, and frankly, kind of annoying.
              </p>
              <p>
                We believe in true freedom. Not "free as in speech with 47 conditions attached" free.
                Not "free but you have to give us credit" free. Not "free but only if you keep it free" free.
              </p>
              <p>
                Just... free. Actually free. Take it. Use it. Change it. Sell it. We don't care.
              </p>
              <p>
                This is code born from anime nostalgia and late-night coding sessions. It exists because
                we wanted it to exist. If it helps someone else, great. If someone makes money off it, cool.
                If someone improves it and shares back, awesome. If they don't, that's fine too.
              </p>
              <p className="font-semibold">
                We're weebs who code. We're not here to enforce licenses. We're here to put commercials
                back into anime and have fun doing it.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* The Fine Print (That Isn't Actually Required) */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="p-6 rounded-xl"
            style={{
              backgroundColor: 'var(--color-bg-secondary)',
              borderColor: 'var(--color-border)',
              borderWidth: '1px',
              borderStyle: 'solid'
            }}
          >
            <h2 className="text-xl font-theme font-bold mb-4" style={{ color: 'var(--color-primary)' }}>
              The Fine Print (That Isn't Actually Required)
            </h2>
            <p className="text-sm mb-4" style={{ color: 'var(--color-text-secondary)' }}>
              This isn't legally required, but just so we're clear:
            </p>
            <ul className="space-y-2 text-sm" style={{ color: 'var(--color-text)' }}>
              <li><strong>No Warranty:</strong> The code is provided "as is". If it breaks, that's on you to fix.</li>
              <li><strong>No Liability:</strong> We're not responsible for anything that happens if you use this code.</li>
              <li><strong>No Support Guarantee:</strong> We might help you. We might not. Depends on our mood and caffeine levels.</li>
              <li><strong>No Take-Backs:</strong> This lasts forever. Heirs, estates, and anyone waving a statute from a jurisdiction that treats copyright as a family heirloom cannot revoke it. Especially not on Tuesday.</li>
              <li><strong>Trademarks:</strong> "theweebcoders" and our project names might be trademarks. You can use the code, but don't pretend to be us.</li>
            </ul>
          </motion.div>
        </div>
      </section>

      {/* How Long Does This Last? */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="p-8 rounded-xl"
            style={{
              backgroundColor: 'var(--color-bg-secondary)',
              borderColor: 'var(--color-border)',
              borderWidth: '1px',
              borderStyle: 'solid'
            }}
          >
            <h2 className="text-2xl md:text-3xl font-theme font-bold mb-6 text-gradient">
              How Long Does This Last?
            </h2>
            <div className="space-y-4" style={{ color: 'var(--color-text)' }}>
              <p>
                Forever. That's the whole answer. Not "until we change our minds." Not "until the
                author dies and a cousin in Germany finds a statute." Forever.
              </p>
              <p>
                This dedication is granted irrevocably, in perpetuity, perpetually, retroactively,
                in divergent world lines, in non-divergent world lines, in parallel world lines,
                in the event of a grandfather paradox, in the event of a grandmother paradox, any
                other paradox, and Tuesdays.
              </p>
              <p>
                If you inherited this code: congratulations? You inherited the dedication with it.
                You do not get a veto. You do not get to send a cease-and-desist because some
                jurisdictions still treat copyright like a family heirloom. The author already
                gave it away. Every copy. Every fork. Every world line.
              </p>
              <p className="font-semibold">
                No take-backs. Not now. Not later. Not after death. Not after a time traveler
                kills anyone's grandparents. Not on Tuesday. The answer is still: do whatever
                you want.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="p-8 rounded-xl"
            style={{
              background: 'var(--gradient-primary)',
              color: 'var(--color-bg)'
            }}
          >
            <h2 className="text-2xl md:text-3xl font-theme font-bold mb-4">
              Questions About the License?
            </h2>
            <p className="mb-6">
              The answer is: Do whatever you want. That's the point.
            </p>
            <p className="text-sm opacity-90">
              But if you really need to ask something, check our <Link to="/about" className="underline font-semibold">About page</Link> for contact info.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Note About Future Legal Language */}
      <section className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div
            className="p-4 rounded-lg text-center text-sm"
            style={{
              backgroundColor: 'var(--color-primary)' + '10',
              borderColor: 'var(--color-primary)' + '30',
              borderWidth: '1px',
              borderStyle: 'solid',
              color: 'var(--color-text-secondary)'
            }}
          >
            <p>
              <strong>Note:</strong> We may update this page in the future with more formal legal language
              (like <a href="https://unlicense.org/" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: 'var(--color-primary)' }}>The Unlicense</a> or <a href="https://creativecommons.org/public-domain/cc0/" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: 'var(--color-primary)' }}>CC0</a>),
              but the intent remains the same: Public domain. Do whatever you want. Forever.
            </p>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default LicensePage;
